import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../lib/apiBase";

const LIVE_STATUSES = new Set(["in_progress", "active", "live"]);
const COMPLETED_STATUSES = new Set(["completed", "finished", "archived"]);
const FOUNDER_EMAIL = "peteywhiskers@yahoo.com";

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function cachedFounderSession() {
  try {
    const cachedUsers = ["member_user", "ga_user", "user"]
      .map((key) => safeJsonParse(localStorage.getItem(key) || ""))
      .filter((value) => value && typeof value === "object");
    const user = cachedUsers.length ? Object.assign({}, ...cachedUsers) : {};
    const email = normalize(
      user?.email ||
        localStorage.getItem("member_email") ||
        localStorage.getItem("email") ||
        ""
    );
    return email === FOUNDER_EMAIL;
  } catch {
    return false;
  }
}

export function useCachedFounderSession() {
  const [isFounder, setIsFounder] = useState(() => cachedFounderSession());

  useEffect(() => {
    const refresh = () => setIsFounder(cachedFounderSession());
    refresh();
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return isFounder;
}

export function tournamentDetailsPath(tournament) {
  const slug = normalize(tournament?.slug);
  const id = normalize(tournament?.id);

  if (slug === "gas-up-3v3-crew-headquarters" || id === "gas-up-3v3-crew-hq-2026") {
    return "/man-cave?event=gas-up-3v3-crew-headquarters";
  }
  if (slug === "get-active-3v3-75-ovr" || id === "get-active-3v3-75-ovr-2026") {
    return "/tournaments";
  }
  return "/man-cave";
}

export function tournamentHubPath(tournament) {
  const slug = String(tournament?.slug || tournament?.id || "").trim();
  if (!slug) return "/man-cave";
  return `/tournaments/${encodeURIComponent(slug)}/live`;
}

export default function useLiveTournamentNavigation({ includeTestMode = false, pollMs = 30000 } = {}) {
  const [tournaments, setTournaments] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(apiUrl("/tournaments"), { cache: "no-store" });
      const data = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(data?.tournaments)) return;
      setTournaments(data.tournaments);
    } catch {
      // Navigation should stay usable if Render is waking up.
    }
  }, []);

  useEffect(() => {
    refresh();
    const intervalId = window.setInterval(refresh, Math.max(10000, Number(pollMs) || 30000));
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refresh);
    };
  }, [pollMs, refresh]);

  return useMemo(() => {
    const running = tournaments
      .filter((tournament) => {
        const status = normalize(tournament?.status);
        if (!LIVE_STATUSES.has(status)) return false;
        if (COMPLETED_STATUSES.has(status) || tournament?.completedAt || tournament?.archivedFromHub) return false;
        if (tournament?.testMode && !includeTestMode) return false;
        return true;
      })
      .sort((a, b) => {
        const testPriority = Number(Boolean(a?.testMode)) - Number(Boolean(b?.testMode));
        if (testPriority !== 0) return testPriority;
        return Number(b?.startedAt || 0) - Number(a?.startedAt || 0);
      });

    const tournament = running[0] || null;
    if (!tournament) return null;

    const isTest = Boolean(tournament.testMode);
    const detailsPath = tournamentDetailsPath(tournament);
    const hubPath = tournamentHubPath(tournament);

    return {
      tournament,
      isTest,
      label: isTest ? "🧪 TEST TOURNAMENT" : "🔴 LIVE TOURNAMENT",
      detailsPath,
      hubPath,
      destinationPath: isTest ? detailsPath : hubPath,
    };
  }, [includeTestMode, tournaments]);
}
